import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { widgetApi, analyticsApi } from "../services/api";
import MetricCard from "../components/MetricCard";
import LoadingSpinner from "../components/LoadingSpinner";
import errorTracker from "../utils/errorTracker";
import authService, { isAdminUser } from "../services/auth";
import { DATE_RANGES } from "../config/constants";
import { formatCost } from "../utils/formatters";
import {
  BotIcon,
  MessageIcon,
  ConversationIcon,
  DollarIcon,
  WidgetIcon,
  SettingsIcon,
} from "../components/NavIcons";
import "./Dashboard.css";

/**
 * Return ISO date strings for an analytics window ending today.
 * @param {number} days - Number of days to look back
 * @returns {{ startDate: string, endDate: string }} e.g. { startDate: "2026-03-15", endDate: "2026-04-15" }
 */
function getAnalyticsDateRange(days) {
  const now = new Date();
  const endDate = now.toISOString().split("T")[0];
  const startDate = new Date(now - days * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];
  return { startDate, endDate };
}

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = isAdminUser(authService.getCurrentUser());

  const loadDashboardStats = useCallback(async (signal) => {
    try {
      // Fetch widgets and usage summary in parallel
      const { startDate, endDate } = getAnalyticsDateRange(
        DATE_RANGES.DEFAULT_ANALYTICS_DAYS,
      );

      const [widgetsResponse, usageResponse] = await Promise.all([
        widgetApi.getWidgets(1, 1, { signal }), // just need total count from pagination
        analyticsApi.getUsageSummary(startDate, endDate, { signal }),
      ]);

      // Note: We only display totalWidgets from pagination.total
      // Active widget count would require fetching all widgets or a dedicated endpoint
      setStats({
        totalWidgets: widgetsResponse.pagination?.total || 0,
        totalMessages: usageResponse.summary?.totalMessages || 0,
        totalConversations: usageResponse.summary?.totalConversations || 0,
        totalCost: usageResponse.summary?.totalCost || 0,
      });
    } catch (err) {
      if (err.name === "AbortError" || err.name === "CanceledError") {
        return;
      }
      errorTracker.captureException(err, { context: "loadDashboardStats" });
      // Don't show error toast - dashboard should still be usable without stats
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadDashboardStats(controller.signal);
    return () => controller.abort();
  }, [loadDashboardStats]);

  return (
    <div className="dashboard-home">
      <h1>Dashboard</h1>
      <p className="subtitle">Welcome to your Kwibl dashboard</p>

      {/* Stats Summary */}
      {loading ? (
        <div className="dashboard-stats-loading">
          <LoadingSpinner size="small" />
        </div>
      ) : stats ? (
        <div className="dashboard-stats">
          <MetricCard
            icon={<BotIcon />}
            label="Total Widgets"
            value={stats.totalWidgets.toLocaleString()}
            color="blue"
          />
          <MetricCard
            icon={<MessageIcon />}
            label="Messages (30d)"
            value={stats.totalMessages.toLocaleString()}
            color="green"
          />
          <MetricCard
            icon={<ConversationIcon />}
            label="Conversations (30d)"
            value={stats.totalConversations.toLocaleString()}
            color="purple"
          />
          {isAdmin && (
            <MetricCard
              icon={<DollarIcon />}
              label="Cost (30d)"
              value={formatCost(stats.totalCost)}
              color="orange"
            />
          )}
        </div>
      ) : (
        <p className="dashboard-stats-unavailable">
          Usage stats are temporarily unavailable.
        </p>
      )}

      <div className="dashboard-grid">
        <Link to="/widgets" className="dashboard-card">
          <div className="card-icon card-icon--blue">
            <WidgetIcon />
          </div>
          <h3>My Widgets</h3>
          <p>Create and manage your chatbot widgets</p>
        </Link>
        <Link to="/settings" className="dashboard-card">
          <div className="card-icon card-icon--gray">
            <SettingsIcon />
          </div>
          <h3>Settings</h3>
          <p>Manage your account and billing</p>
        </Link>
      </div>

      <div className="quick-start">
        <h2>Quick Start</h2>
        <ol>
          <li>Create a new widget and customize its appearance</li>
          <li>Add knowledge sources (files, URLs, or text)</li>
          <li>Copy the embed code and add it to your website</li>
          <li>Monitor conversations and analytics</li>
        </ol>
      </div>
    </div>
  );
};

export default Dashboard;
